import DevToolsIcon from '@clearkit/icons/glyphs/Config';
import ZapIcon from '@clearkit/icons/glyphs/Zap';
import Clearbit from '@clearkit/icons/identity/Clearbit';
import AudiencesNavIcon from '@clearkit/icons/nav/Audiences';
import CompaniesNavIcon from '@clearkit/icons/nav/Companies';
import DashboardIcon from '@clearkit/icons/nav/Dashboard';
import DataEnrichmentNavIcon from '@clearkit/icons/nav/Demo';
import FormsNavIcon from '@clearkit/icons/nav/Forms';
import HelpIcon from '@clearkit/icons/nav/Help';
import IdealCustomerProfileNavIcon from '@clearkit/icons/nav/Icp';
import PeopleNavIcon from '@clearkit/icons/nav/People';
import SandboxNavIcon from '@clearkit/icons/nav/Sandbox';
import SetupNavIcon from '@clearkit/icons/nav/Setup';
import SwitcherNavIcon from '@clearkit/icons/nav/Switcher';
import Popover from '@material-ui/core/Popover';
import RootRef from '@material-ui/core/RootRef';
import { withRouter } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Subscribe } from 'unstated';

import AppSwitch from '~/components/AppSwitch';
import CompanyLogo from '~/components/CompanyLogo';
import UserContainer from '~/containers/User';
import useVisitorReport from '~/hooks/useVisitorReport';
import paths from '~/lib/paths';

import DeveloperPanel from '../DeveloperPanel';
import styles from './Sidebar.style';
import UserMenu from './sidebar/UserMenu/UserMenu';
import SidebarNavLink from './SidebarNavLink';

function SidebarDivider({ className }) {
  return (
    <div
      className={classnames(className, 'border-gray-100 border-t my-1')}></div>
  );
}

const Sidebar = ({
  classes,
  userContainer,
  history: {
    location: { pathname },
  },
}) => {
  const [configOpen, setConfigOpen] = useState(false);
  const [appSwitchOpen, setAppSwitchOpen] = useState(false);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const { enabled: isVisitorReportEnabled } = useVisitorReport();
  const configMenuButton = useRef();
  const appSwitchButton = useRef();

  const renderMenuContents = () => {
    if (userContainer.hasNoAccess()) {
      return <ul className={classes.list} />;
    }

    if (pathname === '/welcome' || userContainer.needsToOnboard()) {
      return (
        <ul className={classes.list}>
          <SidebarNavLink
            className={classes.logoLink}
            classes={classes}
            to="/welcome"
            title="Welcome"
            icon={SetupNavIcon}
          />
        </ul>
      );
    }

    return (
      <ul className={classnames('flex flex-col flex-none gap-1')}>
        {userContainer.shouldShowDashboard() && (
          <SidebarNavLink
            exact
            className={classes.logoLink}
            classes={classes}
            to="/"
            title="Home"
            icon={DashboardIcon}
          />
        )}

        <SidebarDivider />

        {userContainer.shouldShowPeople() && (
          <SidebarNavLink
            classes={classes}
            to="/people"
            title="People"
            icon={PeopleNavIcon}
          />
        )}

        {userContainer.shouldShowCompanies() && (
          <SidebarNavLink
            classes={classes}
            to="/companies"
            title="Companies"
            icon={CompaniesNavIcon}
          />
        )}

        {userContainer.shouldShowICP() && (
          <SidebarNavLink
            classes={classes}
            to="/ideal-customer-profile"
            title="Ideal Customer Profile"
            icon={IdealCustomerProfileNavIcon}
          />
        )}

        <SidebarDivider />

        <SidebarNavLink
          classes={classes}
          to="/enrichment"
          title="Data Sync"
          icon={DataEnrichmentNavIcon}
        />

        <SidebarNavLink
          classes={classes}
          to="/forms"
          title="Forms"
          icon={FormsNavIcon}
        />

        <SidebarNavLink
          classes={classes}
          to={paths.audiences()}
          title="Advertising"
          icon={AudiencesNavIcon}
        />

        <SidebarDivider />

        {userContainer.shouldShowSettings() && (
          <SidebarNavLink
            classes={classes}
            to="/settings/configuration"
            title="Connections"
            iconSize={20}
            icon={ZapIcon}
          />
        )}

        <SidebarDivider />

        <SidebarNavLink
          classes={classes}
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.clearbit.com/hc/en-us/"
          title="Help"
          icon={HelpIcon}
        />

        {isVisitorReportEnabled && (
          <>
            <SidebarDivider />
            <SidebarNavLink
              classes={classes}
              to="/visitor-report"
              title="Visitor Report"
              icon={CompaniesNavIcon}
            />
          </>
        )}
      </ul>
    );
  };

  const domain = userContainer.accountDomain();

  if (pathname.startsWith('/onboarding')) return null;

  return (
    <nav className={classnames(classes.root, 'border-r border-gray-100')}>
      <NavLink className={classes.logo} to="/">
        <Clearbit width="36" height="36" />
      </NavLink>

      {renderMenuContents()}

      <div className={classes.spacer} />

      <ul className={classes.lowerList}>
        {process.env.NODE_ENV === 'development' && (
          <>
            <SidebarNavLink
              classes={classes}
              to="/sandbox"
              title="Sandbox"
              icon={SandboxNavIcon}
            />

            <SidebarNavLink
              classes={classes}
              title="Dev Tools"
              icon={DevToolsIcon}
              href="#none"
              onClick={(e) => {
                e.preventDefault();
                setShowDevPanel(true);
              }}
            />

            <DeveloperPanel
              open={showDevPanel}
              onClose={() => {
                setShowDevPanel(false);
              }}
            />
          </>
        )}

        <RootRef rootRef={appSwitchButton}>
          <SidebarNavLink
            className={classnames(classes.listItem, classes.lowerListItem)}
            classes={classes}
            href="#none"
            title="Switch applications"
            onClick={(e) => {
              e.preventDefault();
              setAppSwitchOpen(true);
            }}
            icon={SwitcherNavIcon}
          />
        </RootRef>

        <RootRef rootRef={configMenuButton}>
          <SidebarNavLink
            className={classnames(
              classes.listItem,
              classes.lowerListItem,
              classes.companyListItem,
            )}
            classes={classes}
            href="#none"
            title="Switch Team"
            onClick={(e) => {
              e.preventDefault();
              setConfigOpen(true);
            }}
            hover={false}
            icon={({ className }) => {
              const company = (
                <CompanyLogo
                  domain={domain}
                  className={classnames(classes.companyIcon, className)}
                  size={28}
                />
              );

              return company;
            }}
          />
        </RootRef>

        <Popover
          classes={{ paper: classes.paper }}
          open={appSwitchOpen}
          anchorEl={appSwitchButton.current}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={() => setAppSwitchOpen(false)}>
          <AppSwitch />
        </Popover>

        <UserMenu
          popoverProps={{
            classes: { paper: classes.paper },
            open: configOpen,
            anchorEl: configMenuButton.current,
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
            onClose: () => setConfigOpen(false),
          }}
        />
      </ul>
    </nav>
  );
};

const WrappedSidebar = (props) => (
  <Subscribe to={[UserContainer]}>
    {(userContainer) => <Sidebar {...props} userContainer={userContainer} />}
  </Subscribe>
);

Sidebar.propTypes = {
  beta: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
  view: PropTypes.string,
  userContainer: PropTypes.shape({
    accountDomain: PropTypes.func.isRequired,
    shouldShowCompanies: PropTypes.func.isRequired,
    shouldShowICP: PropTypes.func.isRequired,
    shouldShowDashboard: PropTypes.func.isRequired,
    shouldShowSettings: PropTypes.func.isRequired,
    shouldShowPeople: PropTypes.func.isRequired,
    shouldShowForms: PropTypes.func.isRequired,
    hasNoAccess: PropTypes.func.isRequired,
    needsToOnboard: PropTypes.func.isRequired,
  }),
};

export default withRouter(withStyles(styles)(WrappedSidebar));
